import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'auto';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  autoClose?: boolean;
  duration?: number;
}

interface AppState {
  // Theme management
  theme: Theme;
  setTheme: (theme: Theme) => void;
  
  // Sidebar management
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  
  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Loading states
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  
  // Error handling
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Theme management
      theme: 'auto' as Theme,
      setTheme: (theme: Theme) => set({ theme }),
      
      // Sidebar management
      sidebarOpen: true,
      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state: AppState) => ({ sidebarOpen: !state.sidebarOpen })),
      
      // Notification management
      notifications: [],
      addNotification: (notification: Omit<Notification, 'id'>) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newNotification: Notification = {
          ...notification,
          id,
          autoClose: notification.autoClose ?? true,
          duration: notification.duration ?? 5000,
        };
        set((state: AppState) => ({
          notifications: [...state.notifications, newNotification]
        }));
        
        if (newNotification.autoClose) {
          setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration);
        }
      },
      removeNotification: (id: string) => set((state: AppState) => ({
        notifications: state.notifications.filter((n: Notification) => n.id !== id)
      })),
      clearNotifications: () => set({ notifications: [] }),
      
      // Loading state management
      isLoading: false,
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      
      // Error management
      error: null,
      setError: (error: string | null) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'app-store',
      partialize: (state: AppState) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
);